import { buildApiUrl } from "../../../config";
import { withCredentials } from "../../../services/apiClient";
import { fetchWithRetry, readApiError } from "../../../utils";

import {
  buildPathWithQuery,
  fetchSlideLibraryJson,
  isEmptyLibraryResponse,
  readJsonResponse,
  readListPayload,
} from "../http";
import { normalizeSlideLibrarySlide } from "../normalizers";
import type {
  SlideLibraryDeleteSlidesRequest,
  SlideLibraryListOptions,
  SlideLibraryReplaceTagRequest,
  SlideLibrarySlide,
  SlideLibrarySlidesResponse,
  SlideLibrarySlideUpdateRequest,
} from "../types";

export async function fetchSlideLibrarySlides(
  options: SlideLibraryListOptions = {},
  requestOptions: Pick<RequestInit, "signal"> = {}
): Promise<SlideLibrarySlidesResponse> {
  const response = await fetchWithRetry(
    buildApiUrl(buildPathWithQuery("/api/finster/slides", options)),
    withCredentials({ method: "GET", signal: requestOptions.signal }),
    1,
    "SlideLibrarySlides"
  );

  if (!response.ok) {
    if (isEmptyLibraryResponse(response)) {
      return { slides: [], totalCount: 0 };
    }
    throw new Error(await readApiError(response));
  }

  const payload = await readJsonResponse(response);
  const data = readListPayload(payload);
  const slides = Array.isArray(data.slides)
    ? data.slides
        .map((slide) => normalizeSlideLibrarySlide(slide))
        .filter((slide): slide is SlideLibrarySlide => Boolean(slide))
    : [];
  return {
    slides,
    totalCount: Number(data.total_count ?? data.totalCount ?? slides.length),
  };
}

export async function deleteSlideLibrarySlides(slideIds: string[]): Promise<void> {
  const request: SlideLibraryDeleteSlidesRequest = { slide_ids: slideIds };
  await fetchSlideLibraryJson<unknown>(
    "/api/finster/slides",
    {
      method: "DELETE",
      body: JSON.stringify(request),
    },
    "SlideLibraryDeleteSlides"
  );
}

export async function replaceSlideLibrarySlideTags(
  slideId: string,
  request: SlideLibraryReplaceTagRequest
): Promise<SlideLibrarySlide | null> {
  const payload = await fetchSlideLibraryJson<unknown>(
    `/api/finster/slides/${encodeURIComponent(slideId)}/tags`,
    {
      method: "POST",
      body: JSON.stringify(request),
    },
    "SlideLibraryReplaceSlideTags"
  );
  const data = readListPayload(payload);
  return normalizeSlideLibrarySlide(data.slide ?? data);
}

export async function updateSlideLibrarySlide(
  slideId: string,
  update: SlideLibrarySlideUpdateRequest
): Promise<SlideLibrarySlide | null> {
  const payload = await fetchSlideLibraryJson<unknown>(
    `/api/finster/slides/${encodeURIComponent(slideId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(update),
    },
    "SlideLibraryUpdateSlide"
  );
  const data = readListPayload(payload);
  return normalizeSlideLibrarySlide(data.slide ?? data);
}
